import { create } from "zustand";
import type { Session, User } from "@supabase/supabase-js";
import { PENDING_PASSWORD_KEY, supabase } from "@/lib/supabase";
import { setSentryUser } from "@/lib/sentry";

export type OrgRole = "owner" | "admin" | "member" | "viewer";

/** Why the set-password screen is showing: arrived from an invite or
 *  reset email, or chose "Change password" from the user menu. */
export type PasswordPrompt = "invite" | "recovery" | "change";

export interface OrgSummary {
  id: string;
  name: string;
  role: OrgRole;
}

interface AuthState {
  /** null = not loaded yet, undefined-like. Use `ready` to distinguish. */
  user: User | null;
  session: Session | null;
  org: OrgSummary | null;
  orgs: OrgSummary[];
  ready: boolean;
  loading: boolean;
  error: string | null;
  passwordPrompt: PasswordPrompt | null;

  bootstrap: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  setPasswordPrompt: (prompt: PasswordPrompt | null) => void;
  signOut: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
}

async function loadOrgs(userId: string): Promise<OrgSummary[]> {
  // memberships join organizations.
  // Typed client arrives once `npm run gen:types` runs against the migrated schema.
  const { data, error } = await supabase
    .from("memberships")
    .select("role, organizations(id, name)")
    .eq("user_id", userId);

  if (error) throw error;

  // supabase-js types foreign-key joins as arrays, though with a to-one
  // relationship it's always a single row.
  type Row = {
    role: OrgRole;
    organizations: { id: string; name: string } | { id: string; name: string }[] | null;
  };
  const rows = (data ?? []) as unknown as Row[];

  return rows
    .map((r) => {
      const orgJoin = Array.isArray(r.organizations) ? r.organizations[0] : r.organizations;
      if (!orgJoin) return null;
      return { id: orgJoin.id, name: orgJoin.name, role: r.role };
    })
    .filter((o): o is OrgSummary => o !== null);
}

const SIGN_OUT_TIMEOUT_MS = 5000;

let authSubscription: { unsubscribe: () => void } | null = null;
let authEpoch = 0;

/** The invite/reset link this user arrived by and hasn't yet set a password
 *  for (recorded in lib/supabase). Bound to the user id, so a link for one
 *  account never prompts whoever else is signed in on this browser. */
function pendingPasswordFor(userId: string | undefined): PasswordPrompt | null {
  if (!userId) return null;
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_PASSWORD_KEY) ?? "null");
    if (pending?.userId !== userId) return null;
    return pending.type === "invite" || pending.type === "recovery" ? pending.type : null;
  } catch {
    return null;
  }
}

function clearPendingPassword() {
  try {
    localStorage.removeItem(PENDING_PASSWORD_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Last resort when the auth client is unresponsive: remove the persisted
 *  Supabase session (sb-<project>-auth-token*) so a reload starts signed out. */
function clearStoredSession() {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith("sb-") && key.includes("-auth-token")) localStorage.removeItem(key);
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  org: null,
  orgs: [],
  ready: false,
  loading: false,
  error: null,
  passwordPrompt: null,

  bootstrap: async () => {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    const user = session?.user ?? null;

    let orgs: OrgSummary[] = [];
    if (user) {
      try {
        orgs = await loadOrgs(user.id);
      } catch (e) {
        console.warn("[auth] loadOrgs failed", e);
      }
    }

    const stored = localStorage.getItem("neo-active-org");
    const active =
      orgs.find((o) => o.id === stored) ?? orgs[0] ?? null;

    set({ user, session, orgs, org: active, ready: true, passwordPrompt: pendingPasswordFor(user?.id) });
    // Tag Sentry crash reports with the signed-in user. No-op if Sentry
    // isn't initialised (no DSN env var).
    setSentryUser(user ? { id: user.id, email: user.email ?? undefined } : null);

    // The callback MUST stay synchronous. supabase-js runs it while holding
    // its auth lock (on every tab refocus and token refresh), so awaiting a
    // Supabase query in here deadlocks the client: sign-out and every later
    // request hang forever. Org loading is deferred until the lock is free.
    authSubscription?.unsubscribe();
    authSubscription = supabase.auth.onAuthStateChange((_event, newSession) => {
      const epoch = ++authEpoch;
      const newUser = newSession?.user ?? null;
      if (!newUser) {
        set({ user: null, session: null, org: null, orgs: [], passwordPrompt: null });
        setSentryUser(null);
        return;
      }
      setSentryUser({ id: newUser.id, email: newUser.email ?? undefined });
      // Same user (token refresh / tab refocus): keep the app mounted.
      const sameUser = get().user?.id === newUser.id;
      if (sameUser) set({ user: newUser, session: newSession });
      // A different user signing in gets their own pending-password state,
      // read when applied (signIn clears it right after this event fires).
      const prompt = () => (sameUser ? {} : { passwordPrompt: pendingPasswordFor(newUser.id) });
      setTimeout(() => {
        loadOrgs(newUser.id)
          .then((nextOrgs) => {
            // A newer event (e.g. sign-out) superseded this one.
            if (epoch !== authEpoch) return;
            set({
              user: newUser,
              session: newSession,
              orgs: nextOrgs,
              org: nextOrgs.find((o) => o.id === get().org?.id) ?? nextOrgs[0] ?? null,
              ...prompt(),
            });
          })
          .catch((e) => {
            console.warn("[auth] onAuthStateChange loadOrgs failed", e);
            if (epoch === authEpoch) set({ user: newUser, session: newSession, ...prompt() });
          });
      }, 0);
    }).data.subscription;
  },

  signIn: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // Signed in with a password, so they already have one.
      clearPendingPassword();
      set({ passwordPrompt: null });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Sign in failed" });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  sendPasswordReset: async (email) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset`,
      });
      if (error) throw error;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Reset failed" });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  updatePassword: async (password) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // A new password should lock out anyone still holding an old session.
      await supabase.auth.signOut({ scope: "others" });
      clearPendingPassword();
      set({ passwordPrompt: null });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Couldn't update password" });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  setPasswordPrompt: (prompt) => set({ passwordPrompt: prompt, error: null }),

  signOut: async () => {
    localStorage.removeItem("neo-active-org");
    clearPendingPassword();
    set({ user: null, session: null, org: null, orgs: [] });
    setSentryUser(null);
    // Revoke server-side, but never let a slow or failed network call keep
    // the session on this device: fall back to dropping it locally.
    const result = await Promise.race([
      supabase.auth.signOut().catch((error: unknown) => ({ error })),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), SIGN_OUT_TIMEOUT_MS)),
    ]);
    if (result === null) clearStoredSession();
    else if (result.error) await supabase.auth.signOut({ scope: "local" });
    // Full reload wipes in-memory caches (react-query, stores, realtime) so
    // the next person on this browser starts clean.
    window.location.assign("/");
  },

  switchOrg: async (orgId) => {
    const next = get().orgs.find((o) => o.id === orgId);
    if (!next) return;
    localStorage.setItem("neo-active-org", orgId);
    set({ org: next });
  },
}));
